import React, { useState } from 'react';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface CreateTicketProps {
  domain: string;
  onCreated: () => void;
}

const CreateTicket: React.FC<CreateTicketProps> = ({ domain, onCreated }) => {
  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    category: '',
    priority: 'Medium',
  });
  const [submitting, setSubmitting] = useState(false);

  const categories = [
    'Academic',
    'Infrastructure',
    'Transport',
    'Fee',
    'Discipline',
    'Hostel',
    'Staff',
    'General',
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.subject.trim() || !formData.description.trim() || !formData.category) {
      alert('Please fill in all required fields.');
      return;
    }

    setSubmitting(true);
    try {
      const userData = localStorage.getItem('userData');
      const parsed = userData ? JSON.parse(userData) : {};

      await addDoc(collection(db, `school-data/${domain}/complaint-tickets`), {
        subject: formData.subject.trim(),
        description: formData.description.trim(),
        category: formData.category,
        priority: formData.priority,
        status: 'open',
        createdBy: parsed.customUserId || parsed.uid || '',
        studentName: parsed.name || 'Unknown',
        className: parsed.class || parsed.className || '',
        section: parsed.section || '',
        enrollmentId: parsed.customUserId || parsed.enrollmentId || '',
        createdAt: Date.now(),
        updatedAt: Date.now(),
      });

      alert('Complaint submitted successfully!');
      onCreated();
    } catch (error) {
      console.error('Error creating ticket:', error);
      alert('Failed to submit complaint. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="row justify-content-center">
      <div className="col-lg-8">
        <div className="card border-0 shadow-sm">
          <div className="card-body p-4">
            <h5 className="card-title mb-4">
              <i className="bi bi-plus-circle me-2 text-primary-custom"></i>
              Submit a Complaint
            </h5>
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label className="form-label fw-semibold">Subject *</label>
                <input
                  type="text"
                  className="form-control"
                  name="subject"
                  placeholder="Brief summary of the complaint or issue"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Category *</label>
                  <select
                    className="form-select"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Category</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Priority</label>
                  <select
                    className="form-select"
                    name="priority"
                    value={formData.priority}
                    onChange={handleChange}
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Urgent">Urgent</option>
                  </select>
                </div>
              </div>

              <div className="mb-4">
                <label className="form-label fw-semibold">Description *</label>
                <textarea
                  className="form-control"
                  name="description"
                  rows={5}
                  placeholder="Describe the issue in detail..."
                  value={formData.description}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="d-flex gap-2">
                <button
                  type="submit"
                  className="btn btn-primary-custom"
                  disabled={submitting}
                >
                  {submitting ? (
                    <><span className="spinner-border spinner-border-sm me-2"></span>Submitting...</>
                  ) : (
                    <><i className="bi bi-send me-2"></i>Submit Complaint</>
                  )}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={onCreated}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateTicket;
